<template>
  <div>
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
      <div>
        <h4 class="mb-0">Tax &amp; Title Deed</h4>
        <small class="text-muted">Property ID #{{ propertyId }}</small>
      </div>

      <b-button
        variant="outline-secondary"
        :to="{ name: 'property-edit', params: { id: propertyId } }"
      >
        <feather-icon icon="ArrowLeftIcon" class="mr-50" />Back to Property
      </b-button>
    </div>

    <b-row>
      <b-col lg="8">
        <b-card no-body class="tax-form-card">
          <title-deed-area-section
            ref="titleDeedAreaSection"
            :property-id="propertyId"
            :project-name.sync="projectName"
            :tower.sync="tower"
            :floor.sync="floor"
            :unit-no.sync="unitNo"
            :unit-area.sync="unitArea"
            :appraised-rate-per-sqm.sync="appraisedRatePerSqm"
            :treasury-condo-id.sync="treasuryCondoId"
            :appraised-value="appraisedValue"
          />

          <hr class="section-divider" />

          <sale-details-section
            :sale-price.sync="salePrice"
            :seller-type.sync="sellerType"
            :date-acquired.sync="dateAcquired"
            :transfer-date.sync="transferDate"
            :is-primary-residence-exempt.sync="isPrimaryResidenceExempt"
            :today="today"
            :holding-years="result.holdingYears"
            :calendar-year="result.calendarYear"
          />

          <hr class="section-divider" />

          <commission-section
            :commission-type.sync="commissionType"
            :commission-rate.sync="commissionRate"
            :commission-manual-amount.sync="commissionManualAmount"
            :commission-include-vat.sync="commissionIncludeVat"
            :show-commission-in-pdf.sync="showCommissionInPdf"
          />

          <hr class="section-divider" />

          <document-details-section
            :owner-name.sync="ownerName"
            :agent-name.sync="agentName"
          />
        </b-card>

        <expense-allocation-card
          :split-fields="splitFields"
          :expense-split="expenseSplit"
          :buyer-amounts="buyerAmounts"
          :seller-amounts="sellerAmounts"
          @set-split="setExpenseSplit"
        />

        <cheque-disbursement-card
          :seller-deductions.sync="sellerDeductions"
          :seller-deductions-total="sellerDeductionsTotal"
          :seller-net-cheque-total="sellerNetChequeTotal"
          :transfer-date="transferDate"
        />

        <cashier-cheque-card
          :cheque-groups.sync="chequeGroups"
          :govt-total="result.total"
          :seller-net-total="sellerNetChequeTotal"
          :commission-total="commissionResult.total"
        />
      </b-col>

      <b-col lg="4">
        <summary-sidebar
          :result="result"
          :commission-type="commissionType"
          :commission-rate="commissionRate"
          :commission-result="commissionResult"
          :commission-include-vat="commissionIncludeVat"
          :buyer-total="buyerTotal"
          :seller-total="sellerTotal"
          :seller-deductions-total="sellerDeductionsTotal"
          :seller-net-cheque-total="sellerNetChequeTotal"
          :preparing-expense-pdf="preparingExpensePdf"
          :preparing-closing-pdf="preparingClosingPdf"
          :is-saving="isSaving"
          @download-expense-pdf="downloadExpenseAllocation"
          @download-closing-pdf="downloadClosingStatement"
          @save="saveTaxInfo"
        />
      </b-col>
    </b-row>

    <expense-allocation-pdf
      v-if="preparingExpensePdf"
      ref="expensePdfContent"
      :property-id="propertyId"
      :property-code="propertyCode"
      :project-name="projectName"
      :unit-no="unitNo"
      :tower="tower"
      :floor="floor"
      :detail-project-name="detailProjectName"
      :detail-unit-no="detailUnitNo"
      :detail-tower="detailTower"
      :detail-floor="detailFloor"
      :unit-area="unitArea"
      :appraised-rate-per-sqm="appraisedRatePerSqm"
      :owner-name="ownerName"
      :agent-name="agentName"
      :sale-price="salePrice"
      :appraised-value="appraisedValue"
      :date-acquired="dateAcquired"
      :transfer-date="transferDate"
      :result="result"
      :seller-type="sellerType"
      :is-primary-residence-exempt="isPrimaryResidenceExempt"
      :show-commission-in-pdf="showCommissionInPdf"
      :commission-type="commissionType"
      :commission-rate="commissionRate"
      :commission-result="commissionResult"
      :commission-include-vat="commissionIncludeVat"
      :title-deed-image-src="titleDeedImageSrc"
    />

    <closing-statement-pdf
      v-if="preparingClosingPdf"
      ref="closingStatementContent"
      :property-id="propertyId"
      :property-code="propertyCode"
      :project-name="projectName"
      :unit-no="unitNo"
      :floor="floor"
      :tower="tower"
      :detail-project-name="detailProjectName"
      :detail-unit-no="detailUnitNo"
      :detail-tower="detailTower"
      :detail-floor="detailFloor"
      :sale-price="salePrice"
      :result="result"
      :buyer-amounts="buyerAmounts"
      :seller-amounts="sellerAmounts"
      :buyer-transfer-total="buyerTransferTotal"
      :cheque-groups="chequeGroups"
      :commission-total="commissionResult.total"
      :seller-deductions="sellerDeductions"
      :seller-deductions-total="sellerDeductionsTotal"
      :seller-net-cheque-total="sellerNetChequeTotal"
    />
  </div>
</template>

<script>
import router from '@/router'
import axios from '@axios'
import html2pdf from 'html2pdf.js'
import { calculateCondoTax } from '@/utils/condoTax'
import {
  BButton, BRow, BCol, BCard,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  createDefaultChequeGroups, normalizeChequeGroups, chequeGroupTotal, chequeAmount,
} from './chequeGroups'
import TitleDeedAreaSection from './sections/TitleDeedAreaSection.vue'
import SaleDetailsSection from './sections/SaleDetailsSection.vue'
import CommissionSection from './sections/CommissionSection.vue'
import DocumentDetailsSection from './sections/DocumentDetailsSection.vue'
import ExpenseAllocationCard from './sections/ExpenseAllocationCard.vue'
import ChequeDisbursementCard from './sections/ChequeDisbursementCard.vue'
import CashierChequeCard from './sections/CashierChequeCard.vue'
import SummarySidebar from './sections/SummarySidebar.vue'
import ExpenseAllocationPdf from './pdf/ExpenseAllocationPdf.vue'
import ClosingStatementPdf from './pdf/ClosingStatementPdf.vue'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BCard,
    TitleDeedAreaSection,
    SaleDetailsSection,
    CommissionSection,
    DocumentDetailsSection,
    ExpenseAllocationCard,
    ChequeDisbursementCard,
    CashierChequeCard,
    SummarySidebar,
    ExpenseAllocationPdf,
    ClosingStatementPdf,
  },
  data() {
    return {
      propertyId: router.currentRoute.params.id,
      today: new Date().toISOString().slice(0, 10),

      unitArea: null,
      appraisedRatePerSqm: null,

      projectName: null,
      tower: null,
      floor: null,
      unitNo: null,
      ownerName: null,
      agentName: null,
      propertyCode: null,
      treasuryCondoId: null,

      // Live property-detail values (always fresh from getdetail), used only for the
      // PDF's property line — the fields above reflect the saved tax record instead.
      detailProjectName: null,
      detailUnitNo: null,
      detailTower: null,
      detailFloor: null,

      salePrice: null,
      commissionType: 'percentage', // percentage | manual
      commissionRate: 3,
      commissionManualAmount: null,
      commissionIncludeVat: true,
      showCommissionInPdf: true,
      dateAcquired: null,
      transferDate: new Date().toISOString().slice(0, 10),
      sellerType: 'individual',
      isPrimaryResidenceExempt: false,

      // Thai market convention: buyer pays the selling price, transfer fee is typically split 50/50,
      // seller pays SBT/stamp duty, withholding tax, and the agent's commission
      expenseSplit: {
        sellingPrice: 100,
        transferFee: 50,
        sbtOrStampDuty: 0,
        withholdingTax: 0,
        commission: 0,
      },
      isSaving: false,
      preparingExpensePdf: false,
      preparingClosingPdf: false,
      titleDeedImageSrc: null,

      chequeGroups: createDefaultChequeGroups(),
      sellerDeductions: [],
    }
  },
  created() {
    this.fetchTaxInfo()
  },
  computed: {
    appraisedValue() {
      return (this.unitArea || 0) * (this.appraisedRatePerSqm || 0)
    },
    result() {
      return calculateCondoTax({
        salePrice: this.salePrice,
        appraisedValue: this.appraisedValue,
        dateAcquired: this.dateAcquired,
        transferDate: this.transferDate,
        sellerType: this.sellerType,
        isPrimaryResidenceExempt: this.isPrimaryResidenceExempt,
      })
    },
    commissionResult() {
      const commission = this.commissionType === 'manual'
        ? (this.commissionManualAmount || 0)
        : (this.salePrice || 0) * ((this.commissionRate || 0) / 100)
      const vat = this.commissionIncludeVat ? commission * 0.07 : 0
      return {
        commission,
        vat,
        total: commission + vat,
      }
    },
    splitFields() {
      return [
        { key: 'transferFee', label: 'Transfer Fee', rate: '2%' },
        {
          key: 'sbtOrStampDuty',
          label: this.result.sbtOrStampDuty.type === 'sbt' ? 'Specific Business Tax' : 'Stamp Duty',
          rate: this.result.sbtOrStampDuty.type === 'sbt' ? '3.3%' : '0.5%',
        },
        { key: 'withholdingTax', label: 'Withholding Tax', rate: null },
        { key: 'commission', label: 'Commission & VAT', rate: null },
      ]
    },
    splitBaseAmounts() {
      return {
        sellingPrice: this.salePrice || 0,
        transferFee: this.result.transferFee,
        sbtOrStampDuty: this.result.sbtOrStampDuty.amount,
        withholdingTax: this.result.withholdingTax,
        commission: this.commissionResult.total,
      }
    },
    buyerAmounts() {
      const amounts = {}
      Object.keys(this.splitBaseAmounts).forEach(key => {
        amounts[key] = this.splitBaseAmounts[key] * (this.expenseSplit[key] / 100)
      })
      return amounts
    },
    sellerAmounts() {
      const amounts = {}
      Object.keys(this.splitBaseAmounts).forEach(key => {
        amounts[key] = this.splitBaseAmounts[key] * ((100 - this.expenseSplit[key]) / 100)
      })
      return amounts
    },
    buyerTotal() {
      return Object.values(this.buyerAmounts).reduce((sum, v) => sum + v, 0)
    },
    sellerTotal() {
      return Object.values(this.sellerAmounts).reduce((sum, v) => sum + v, 0)
    },
    buyerTransferTotal() {
      return this.buyerAmounts.sellingPrice
        + this.buyerAmounts.transferFee
        + this.buyerAmounts.sbtOrStampDuty
        + this.buyerAmounts.withholdingTax
    },
    sellerDeductionsTotal() {
      return this.sellerDeductions.reduce((sum, ded) => sum + Number(ded.amount || 0), 0)
    },
    sellerNetChequeTotal() {
      return this.buyerTransferTotal - this.result.total + this.sellerDeductionsTotal
    },
    govtChequeTotal() {
      const group = this.chequeGroupByKind('government')
      return group ? chequeGroupTotal(group, this.result.total) : 0
    },
  },
  methods: {
    setExpenseSplit(key, value) {
      this.expenseSplit[key] = value
    },
    chequeGroupByKind(kind) {
      return this.chequeGroups.find(g => g.kind === kind)
    },
    setGroupCheques(kind, cheques) {
      this.chequeGroups = this.chequeGroups.map(g => (g.kind === kind ? { ...g, cheques } : g))
    },
    resolvedSellerCheques() {
      const group = this.chequeGroupByKind('seller')
      if (!group) return []
      return group.cheques.map((cheque, i) => ({
        name: cheque.name,
        amount: chequeAmount(group, i, this.sellerNetChequeTotal),
      }))
    },
    async downloadExpenseAllocation() {
      const deed = this.$refs.titleDeedAreaSection?.$refs?.titleDeedComponent?.deed
      const deedExt = deed?.filename?.split('.').pop()?.toLowerCase()
      const isDeedImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(deedExt)
      this.titleDeedImageSrc = deed && isDeedImage ? deed.src : null

      this.preparingExpensePdf = true
      await this.$nextTick()

      const element = this.$refs.expensePdfContent.$el
      const opt = {
        margin: 0.35,
        filename: `property-tax-calculation-${this.propertyId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }

      await html2pdf().from(element).set(opt).save()

      this.preparingExpensePdf = false
      this.titleDeedImageSrc = null
    },
    async downloadClosingStatement() {
      this.preparingClosingPdf = true
      await this.$nextTick()

      const element = this.$refs.closingStatementContent.$el
      const opt = {
        margin: 0.35,
        filename: `payment-details-${this.propertyId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }

      await html2pdf().from(element).set(opt).save()

      this.preparingClosingPdf = false
    },
    async fetchTaxInfo() {
      try {
        const { data } = await axios.get(`property/v1/gettaxinfo/${this.propertyId}`)
        const info = data?.data

        if (info) {
          this.unitArea = info.unit_area !== null ? Number(info.unit_area) : null
          this.appraisedRatePerSqm = info.appraised_rate_per_sqm !== null ? Number(info.appraised_rate_per_sqm) : null
          this.projectName = info.project_name || null
          this.tower = info.tower || null
          this.floor = info.floor !== null && info.floor !== undefined ? String(info.floor) : null
          this.unitNo = info.unit_no || null
          this.ownerName = info.owner_name || null
          this.agentName = info.agent_name || null
          this.treasuryCondoId = info.treasury_condo_id || null
          this.salePrice = info.sale_price !== null ? Number(info.sale_price) : null
          this.dateAcquired = info.date_acquired ? String(info.date_acquired).slice(0, 10) : null
          this.transferDate = info.transfer_date ? String(info.transfer_date).slice(0, 10) : this.transferDate
          this.sellerType = info.seller_type || this.sellerType
          this.isPrimaryResidenceExempt = Boolean(info.is_primary_residence_exempt)

          if (info.selling_price_buyer_pct !== null && info.selling_price_buyer_pct !== undefined) {
            this.expenseSplit.sellingPrice = Number(info.selling_price_buyer_pct)
          }
          if (info.transfer_fee_buyer_pct !== null && info.transfer_fee_buyer_pct !== undefined) {
            this.expenseSplit.transferFee = Number(info.transfer_fee_buyer_pct)
          }
          if (info.sbt_stamp_duty_buyer_pct !== null && info.sbt_stamp_duty_buyer_pct !== undefined) {
            this.expenseSplit.sbtOrStampDuty = Number(info.sbt_stamp_duty_buyer_pct)
          }
          if (info.withholding_tax_buyer_pct !== null && info.withholding_tax_buyer_pct !== undefined) {
            this.expenseSplit.withholdingTax = Number(info.withholding_tax_buyer_pct)
          }
          if (info.commission_buyer_pct !== null && info.commission_buyer_pct !== undefined) {
            this.expenseSplit.commission = Number(info.commission_buyer_pct)
          }
          if (Array.isArray(info.cheque_groups) && info.cheque_groups.length) {
            // Full breakdown (groups, names, remarks, manual toggles) saved by this feature.
            this.chequeGroups = normalizeChequeGroups(info.cheque_groups)
          } else {
            // Older rows saved before `cheque_groups` existed: reconstruct what we can
            // from the legacy single-amount / payees-only columns.
            if (info.govt_cheque_amount !== null && info.govt_cheque_amount !== undefined) {
              this.setGroupCheques('government', [
                { name: '', remark: '', amount: Number(info.govt_cheque_amount) },
              ])
            }
            if (Array.isArray(info.seller_cheque_payees)) {
              this.setGroupCheques('seller', info.seller_cheque_payees.map(payee => ({
                name: payee.name || '',
                remark: '',
                amount: payee.amount !== null && payee.amount !== undefined ? Number(payee.amount) : null,
              })))
            }
          }
          if (Array.isArray(info.seller_deductions)) {
            this.sellerDeductions = info.seller_deductions.map(ded => ({
              id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
              label: ded.label || '',
              amount: ded.amount !== null && ded.amount !== undefined ? Number(ded.amount) : null,
              calc: null,
            }))
          }
        }

        // Always run: propertyCode (and the ownerName fallback) only ever come from
        // getdetail, never from gettaxinfo, so this can't be gated on missing tax fields.
        await this.fetchPropertyDetail()
      } catch (err) {
        console.error('Error fetching tax info:', err)
      }
    },
    async fetchPropertyDetail() {
      try {
        const { data } = await axios.get(`property/v1/getdetail/${this.propertyId}`)
        const detail = data?.data?.[0]

        if (detail) {
          if (this.salePrice === null && detail.sale_price) {
            this.salePrice = Number(detail.sale_price)
          }
          if (this.unitArea === null && detail.sqm) {
            this.unitArea = Number(detail.sqm)
          }
          // Property Information section follows the saved tax record: only fill in from
          // getdetail when nothing was saved.
          if (this.projectName === null) {
            this.projectName = detail.place_name || detail.place_name_th || null
          }
          if (this.tower === null) {
            this.tower = detail.buildiing || null
          }
          if (this.floor === null && detail.floors !== null && detail.floors !== undefined) {
            this.floor = String(detail.floors)
          }

          // The PDF's property line always reflects the live property detail, regardless
          // of what's saved in the tax record.
          this.detailProjectName = detail.place_name || detail.place_name_th || null
          this.detailUnitNo = detail.address_no && detail.units
            ? `${detail.address_no} (${detail.units})`
            : detail.address_no || detail.units || null
          this.detailTower = detail.buildiing || null
          this.detailFloor = detail.floors !== null && detail.floors !== undefined ? String(detail.floors) : null

          if (this.ownerName === null && (detail.owner_firstname || detail.owner_lastname)) {
            this.ownerName = `${detail.owner_firstname || ''} ${detail.owner_lastname || ''}`.trim()
          }
          if (this.propertyCode === null && detail.property_code) {
            this.propertyCode = detail.property_code
          }
        }
      } catch (err) {
        console.error('Error fetching property detail:', err)
      }
    },
    async saveTaxInfo() {
      this.isSaving = true

      try {
        await axios.post(`property/v1/savetaxinfo/${this.propertyId}`, {
          unitArea: this.unitArea,
          appraisedRatePerSqm: this.appraisedRatePerSqm,
          projectName: this.projectName,
          tower: this.tower,
          floor: this.floor,
          unitNo: this.unitNo,
          ownerName: this.ownerName,
          agentName: this.agentName,
          treasuryCondoId: this.treasuryCondoId,
          salePrice: this.salePrice,
          dateAcquired: this.dateAcquired,
          transferDate: this.transferDate,
          sellerType: this.sellerType,
          isPrimaryResidenceExempt: this.isPrimaryResidenceExempt,
          sellingPriceBuyerPct: this.expenseSplit.sellingPrice,
          transferFeeBuyerPct: this.expenseSplit.transferFee,
          sbtStampDutyBuyerPct: this.expenseSplit.sbtOrStampDuty,
          withholdingTaxBuyerPct: this.expenseSplit.withholdingTax,
          commissionBuyerPct: this.expenseSplit.commission,
          govtChequeAmount: this.govtChequeTotal,
          sellerChequePayees: this.resolvedSellerCheques(),
          chequeGroups: this.chequeGroups,
          sellerDeductions: this.sellerDeductions.map(ded => ({
            label: ded.label,
            amount: ded.amount,
          })),
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Tax info saved',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: err.response?.data?.message || 'Failed to save tax info',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        console.error('Error saving tax info:', err)
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style src="./propertyTaxForm.css"></style>
